import React, { useEffect } from 'react'
import dynamic from 'next/dynamic'
import Image from 'next/image' // Import next/image
import usePageContext from '../../Page/context'
import { BlocksRenderer } from '@strapi/blocks-react-renderer'
import { getLargestImageUrl } from '../../../../utils'

// Dynamically import Videoplayer with SSR disabled
const VideoPlayer = dynamic(() => import('../../Videoplayer'), { ssr: false })

const ModuleMedallist = ({ medallistData }: { medallistData: any }) => {
  const {
    state: { getEnvConfig },
    dispatch
  } = usePageContext()

  const [isThemeSet, setIsThemeSet] = React.useState(false)

  const renderMedalType = () => {
    if (medallistData.attributes?.medalTypeTitle && medallistData.attributes?.medalTypeValue) {
      return `${medallistData.attributes?.medalTypeTitle}:`
    }
    if (medallistData.attributes?.medalTypeTitle && !medallistData.attributes?.medalTypeValue) {
      return `${medallistData.attributes?.medalTypeTitle}`
    }
    return null
  }

  // Set theme on mount
  useEffect(() => {
    if (!isThemeSet) {
      dispatch({
        type: 'SET_THEME',
        payload: 'black' // Keep theme setting logic
      })
      setIsThemeSet(true)
    }
  }, [isThemeSet, dispatch])

  if (!medallistData?.id) {
    return null
  }

  return (
    <div className='pt-[155px] pb-10 px-4'>
      {/* Top Section: Text + Main Image */}
      <div className=''>
        {/* Text Column */}
        <div className='w-full'>
          <h1 className='font-abcd uppercase font-extrabold text-[108.65px] leading-[97%] tracking-[0%] text-center'>
            {medallistData.attributes?.name}
            {medallistData.attributes?.name && medallistData.attributes?.role ? ', ' : ''}
            <br />
            {medallistData.attributes?.role || ''}
          </h1>
          {medallistData.attributes?.description && (
            <div className='font-swinton font-normal text-[41.29px] leading-[100%] tracking-[-1px] text-center'>
              <BlocksRenderer
                content={medallistData.attributes?.description}
                blocks={{
                  paragraph: ({ children }) => (
                    <p className='mb-2 text-[41.29px] leading-[100%] tracking-[-1px] last:mb-0'>{children}</p>
                  )
                }}
              />
            </div>
          )}
          <h3 className='font-abcd uppercase font-extrabold text-[54.33px] leading-[80%] tracking-[-1px] text-center'>
            {renderMedalType()}
            {medallistData.attributes?.medalTypeTitle && medallistData.attributes?.medalTypeValue && <br />}
            {medallistData.attributes?.medalTypeValue || ''}
          </h3>
          {medallistData.attributes?.medalDescription && (
            <div className='font-swinton font-normal text-[41px] leading-[100%] tracking-[-1px] text-center'>
              <BlocksRenderer
                content={medallistData.attributes.medalDescription}
                blocks={{
                  paragraph: ({ children }) => <p className='mb-2 last:mb-0'>{children}</p>
                }}
              />
            </div>
          )}
          <div className="w-[25px] h-[44px] bg-[url('/svgs/ab-awards-logo-only.svg')] bg-no-repeat bg-center bg-cover mx-auto" />
        </div>

        {/* Image Column */}
        <div className='w-full'>
          {medallistData.attributes?.backgroundForProfileImage?.data?.attributes?.formats && (
            <div className='relative aspect-[4/5] w-full'>
              <Image
                src={getLargestImageUrl(medallistData.attributes?.backgroundForProfileImage?.data?.attributes?.formats)} // Placeholder
                alt={medallistData.attributes.name || ''}
                layout='fill'
                objectFit='cover'
              />
            </div>
          )}
        </div>
      </div>

      {/* Gallery Section */}
      {/* Gallery Item 1 (Landscape - Spans 2 cols) */}

      {medallistData.attributes?.image1Large?.data?.attributes?.formats && (
        <div className='relative aspect-[4/3] w-full md:col-span-2'>
          <Image
            src={getLargestImageUrl(medallistData.attributes?.image1Large?.data?.attributes?.formats)} // Placeholder
            alt={medallistData.attributes.name || ''}
            layout='fill'
            objectFit='cover'
          />
        </div>
      )}

      {/* Gallery Item 2 (Portrait - Left col) */}

      {medallistData.attributes?.image2Left?.data?.attributes?.formats && (
        <div className='relative aspect-[3/4] w-full'>
          <Image
            src={getLargestImageUrl(medallistData.attributes?.image2Left?.data?.attributes?.formats)} // Placeholder
            alt={medallistData.attributes.name || ''}
            layout='fill'
            objectFit='cover'
          />
        </div>
      )}

      {/* Gallery Item 3 (Portrait - Right col) */}

      {medallistData.attributes?.image3Right?.data?.attributes?.formats && (
        <div className='relative aspect-[3/4] w-full'>
          <Image
            src={getLargestImageUrl(medallistData.attributes?.image3Right?.data?.attributes?.formats)} // Placeholder
            alt={medallistData.attributes.name || ''}
            layout='fill'
            objectFit='cover'
          />
        </div>
      )}

      {/* Video Item (Landscape - Spans 2 cols) */}

      {medallistData.attributes?.video?.data?.attributes?.provider_metadata?.public_id && (
        <div className='relative w-full h-full'>
          <VideoPlayer
            autoPlay={false} // Don't autoplay gallery video by default
            loop={false}
            controls // Show controls for gallery video
            id='medallistDetailVideoPlayer'
            publicId={medallistData.attributes?.video?.data?.attributes?.provider_metadata?.public_id} // Provided video ID
            cloudName={getEnvConfig.NEXT_CLOUDINARY_CLOUD_NAME}
            playerConfig={{
              muted: true, // Start muted
              theme: 'light',
              posterOptions: {}
            }}
          />
        </div>
      )}
    </div>
  )
}

export default ModuleMedallist
